// Claiming a device — adding one that already exists on the server to THIS user.
//
// Two ways in, one code path:
//   • a share QR from another user   ("u%#<sharerUserId>n%#<uniqueNumber>")
//   • typing a Unique Number by hand
//
// Port of shareDeviceFunctions.dart. The work is not just "insert a mapping":
// the device sits in a location and a room that the new user may not be mapped
// to, and without those the device would be claimed but invisible — it would
// belong to you and appear in no room. So the location and room mappings are
// backfilled first, then the device mapping.

import {
  getDeviceDetail, getDevicesByUser, getLocations, getRooms,
  addUserLocationMapping, addLocationRoomsMapping, addUserDeviceMapping,
} from '../api/api';

const asInt = (v) => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isNaN(n) ? 0 : n;
};

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

// "u%#12n%#5585891" -> "5585891". A bare unique number is accepted too, so a
// plain-text code or a hand-typed number both work.
export function parseSharedUniqueNumber(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const idx = s.indexOf('n%#');
  if (idx !== -1) {
    const uno = s.slice(idx + 3).trim();
    return uno || null;
  }
  return /^[A-Za-z0-9_-]+$/.test(s) ? s : null;
}

// The payload the owner's QR encodes, so the share screen and the scanner agree.
export const buildShareCode = (userId, uniqueNumber) =>
  `u%#${userId}n%#${uniqueNumber}`;

// -> { ok, title, message, device? }
export async function claimDevice(myUserId, rawCode) {
  const uno = parseSharedUniqueNumber(rawCode);
  if (!uno) {
    return { ok: false, title: 'Invalid Code', message: "That doesn't look like a device code." };
  }

  // 1. Does the device exist?
  const detail = rows(await getDeviceDetail(uno).catch(() => null))[0];
  if (!detail) {
    return {
      ok: false,
      title: 'Device Not Found',
      message: `No device found with unique number ${uno}.`,
    };
  }

  const installedId = detail.ID ?? detail.Id;
  if (!installedId) {
    return {
      ok: false,
      title: 'Device Not Found',
      message: 'The device record is incomplete, so it cannot be added.',
    };
  }

  // 2. Already mine? Claiming twice would create a duplicate mapping row.
  const mine = rows(await getDevicesByUser(myUserId).catch(() => null));
  if (mine.some((d) => String(d.UniqueNumber) === String(uno))) {
    return {
      ok: false,
      title: 'Already Added',
      message: 'This device is already in your account.',
    };
  }

  // 3. Backfill the location mapping, or the device lands somewhere invisible.
  const locationId = detail.LocationID ?? detail.LocationId;
  if (asInt(locationId) > 0) {
    const myLocations = rows(await getLocations(myUserId).catch(() => null));
    const hasLocation = myLocations
      .some((l) => String(l.LocationID) === String(locationId));
    if (!hasLocation) {
      await addUserLocationMapping(myUserId, locationId).catch(() => {});
    }

    // 4. Same for the room (a common device has none — RoomID 0).
    const roomId = detail.RoomID ?? detail.RoomId;
    if (asInt(roomId) > 0) {
      const myRooms = rows(await getRooms(myUserId, locationId).catch(() => null));
      const hasRoom = myRooms.some((r) => String(r.RoomID) === String(roomId));
      if (!hasRoom) {
        await addLocationRoomsMapping(myUserId, locationId, roomId).catch(() => {});
      }
    }
  }

  // 5. Finally map the device itself.
  try {
    await addUserDeviceMapping(myUserId, installedId);
  } catch (e) {
    return {
      ok: false,
      title: "Couldn't Add Device",
      message: 'Something went wrong while adding the device. Please try again.',
    };
  }

  return {
    ok: true,
    title: 'Device Added',
    message: `${detail.DeviceName || uno} has been added to your account.`,
    device: detail,
  };
}
